import React, { useCallback, useMemo, useState } from 'react';
import type { GetServerSideProps } from 'next';
import NextHead from 'next/head';
import NextImage from 'next/image';
import {
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  ImageProps,
  Link,
  Text,
} from '@chakra-ui/react';
import { TriangleDownIcon } from '@chakra-ui/icons';
import { Swiper as SwiperClass, HashNavigation, Lazy, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/lazy';
import * as yup from 'yup';
import prisma from '../../../server/libs/prisma';
import type { NextPageWithLayout } from '../../../common/types';
import type { DiscloseScope } from '../../../common/libs/discloseScope';
import {
  ArticleType,
  createArticleImageUrl,
  createArticleUrl,
} from '../../../common/libs/article';
import useApiClient from '../../../hooks/useApiClient';
import { ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT } from '../../../common/constants';

type ArticlePage = {
  articleId: string;
  pageNumber: number;
  slideText: string | null;
  imgL: string | null;
};

type ArticleHeader = {
  articleId: string;
  discloseScope: DiscloseScope;
  type: ArticleType;
  pageCount: number;
  pages: ArticlePage[];
  updateDateEpochMilliseconds: number;
};

type PageContext = {
  article: ArticleHeader;
  isPreview: boolean;
};

function PageHead({ article }: { article: ArticleHeader }) {
  const articleUrl = createArticleUrl(article.articleId);
  return (
    <NextHead>
      <link rel="canonical" href={articleUrl} />
    </NextHead>
  );
}

function SlideImage({ src, alt = '' }: ImageProps) {
  return (
    <Image
      h="calc(100vh - 38px)"
      w="95vw"
      className="swiper-lazy"
      data-src={src}
      alt={alt}
      objectFit="contain"
      _after={{
        // HACK: broken imageを隠す
        // https://bitsofco.de/styling-broken-images/
        content: '""',
        d: 'block',
        bg: '#333',
        pos: 'absolute',
        top: 0,
        left: 0,
        w: '100%',
        h: '100%',
      }}
    />
  );
}

function PagingButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const rotate = direction === 'left' ? '90deg' : '270deg';
  return (
    <IconButton
      aria-label={direction}
      icon={<TriangleDownIcon rotate={rotate} transform="auto" color="white" />}
      minW={7}
      h="38px"
      variant="ghost"
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
      _focus={{ boxShadow: 'none' }}
      onClick={onClick}
    />
  );
}

function LogoImage() {
  return (
    <NextImage
      src="/images/logo_slide_white_small.png"
      alt="Antaa Slide"
      width={102}
      height={22}
    />
  );
}

function SlideSwiper({
  article,
  isPreview,
}: {
  article: ArticleHeader;
  isPreview: boolean;
}) {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { apiClient } = useApiClient();

  const loginUrl = useMemo(() => {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}#${article.pages.length}`,
    );
    return `/login?redirectUrl=${redirectUrl}`;
  }, [article]);
  const articleUrl = createArticleUrl(article.articleId);
  const articlePages = useMemo(
    () =>
      article.discloseScope === 'PARTIAL'
        ? article.pages.slice(0, ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT)
        : article.pages,
    [article],
  );
  const shouldShowNoLogin =
    article.discloseScope === 'PARTIAL' &&
    article.pageCount > ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT;

  const incrementSessionCount = useCallback(
    async (articleId: string, pageNo: number) => {
      if (!isPreview) {
        await apiClient(`/api/article/count-view/${articleId}/${pageNo}`).catch(
          (_e) => {},
        );
      }
    },
    [apiClient, isPreview],
  );

  const onSwiper = useCallback(
    (swiper: SwiperClass) => {
      setSwiper(swiper);
      setActiveIndex(swiper.activeIndex);
      incrementSessionCount(article.articleId, swiper.activeIndex + 1);
    },
    [setSwiper, setActiveIndex, incrementSessionCount, article.articleId],
  );

  const onActiveIndexChange = useCallback(
    (swiper: SwiperClass) => {
      setActiveIndex(swiper.activeIndex);
      incrementSessionCount(article.articleId, swiper.activeIndex + 1);
    },
    [setActiveIndex, incrementSessionCount, article.articleId],
  );

  const slidePrev = useCallback(() => {
    swiper?.slidePrev();
  }, [swiper]);

  const slideNext = useCallback(() => {
    swiper?.slideNext();
  }, [swiper]);

  return (
    <>
      <Box bg="gray.100">
        <Swiper
          slidesPerView={1}
          modules={[Scrollbar, Lazy, HashNavigation]}
          preloadImages={false}
          scrollbar={{
            hide: false,
            draggable: true,
            snapOnRelease: true,
          }}
          lazy={{
            loadPrevNext: true,
            loadOnTransitionStart: true,
            loadPrevNextAmount: 1,
          }}
          hashNavigation={{ replaceState: true, watchState: true }}
          onSwiper={onSwiper}
          onActiveIndexChange={onActiveIndexChange}
          style={{
            background: '#eee',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: '38px',
          }}
        >
          {articlePages.map((page) => (
            <SwiperSlide key={page.pageNumber} data-hash={page.pageNumber}>
              <Center>
                <SlideImage
                  src={`/api/article/view-image/${page.articleId}${page.imgL}?v=${article.updateDateEpochMilliseconds}`}
                />
              </Center>
            </SwiperSlide>
          ))}
          {shouldShowNoLogin && (
            <SwiperSlide
              data-hash="nologin"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Center>
                <Link href={loginUrl} isExternal>
                  <SlideImage
                    src="/images/slide_require_account_signup_without_click_message.png"
                    alt="続きをご覧いただくにはログインしてください"
                  />
                </Link>
              </Center>
            </SwiperSlide>
          )}
        </Swiper>

        <Flex
          align="center"
          bg="gray.800"
          h="38px"
          pos="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex="overlay"
        >
          <Box flex={1} />

          <HStack flexGrow={0} flexShrink={0} flexBasis="auto" spacing={0}>
            <PagingButton direction="left" onClick={slidePrev} />
            <Text color="white" fontSize="sm" fontWeight="bold">
              {activeIndex + 1}
              <Text as="span" mx={1}>
                /
              </Text>
              {article.pages.length}
            </Text>
            <PagingButton direction="right" onClick={slideNext} />
          </HStack>

          <Flex flex={1} justify="flex-end" align="center">
            <Link href={articleUrl} isExternal>
              <Center p={2}>
                <LogoImage />
              </Center>
            </Link>
          </Flex>
        </Flex>
      </Box>
    </>
  );
}

function SlideVideo({ article }: { article: ArticleHeader }) {
  const loginUrl = useMemo(() => {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}`,
    );
    return `/login?redirectUrl=${redirectUrl}`;
  }, [article]);
  const articleUrl = createArticleUrl(article.articleId);
  const bgUrl = createArticleImageUrl(
    article.articleId,
    article.pages[0].imgL,
    { version: article.updateDateEpochMilliseconds },
  );
  const videoUrl = `/api/article/view-video/${article.articleId}/${article.pages[1].imgL}`;

  return (
    <>
      {article.discloseScope === 'FULL' ? (
        <video
          controls
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
          }}
        >
          <source src={videoUrl} />
        </video>
      ) : (
        <Link href={loginUrl} isExternal>
          <Box pos="relative">
            <Image
              alt="動画を視聴するにはログインが必要です。"
              src={bgUrl || undefined}
              w="full"
            />
            <Center pos="absolute" inset={0} bg="blackAlpha.700" p={4}>
              <Text color="white">動画を視聴するにはログインが必要です。</Text>
            </Center>
          </Box>
        </Link>
      )}
      <Flex
        justify="flex-end"
        align="center"
        bg="gray.800"
        h="38px"
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="overlay"
      >
        <Link href={articleUrl} isExternal>
          <Center p={2}>
            <LogoImage />
          </Center>
        </Link>
      </Flex>
    </>
  );
}

const ArticleEmbedPage: NextPageWithLayout<PageContext> = ({
  article,
  isPreview,
}) => {
  return (
    <>
      <PageHead article={article} />
      {article.type === 'VIDEO' ? (
        <SlideVideo article={article} />
      ) : (
        <SlideSwiper article={article} isPreview={isPreview} />
      )}
    </>
  );
};

ArticleEmbedPage.getLayout = (page) => {
  return <>{page}</>;
};

export default ArticleEmbedPage;

async function incrementContentView(articleId: string) {
  await prisma.article_header.update({
    where: {
      article_id: articleId,
    },
    data: {
      view_count: {
        increment: 1,
      },
    },
  });
}

async function findArticleById(articleId: string) {
  return await prisma.article_header.findUnique({
    where: {
      article_id: articleId,
    },
    include: {
      article_page: true,
    },
  });
}

export const getServerSideProps: GetServerSideProps<PageContext> = async (
  context,
) => {
  const articleId = yup.string().validateSync(context.params?.articleId);
  const preview = yup
    .string()
    .optional()
    .default(null)
    .nullable()
    .validateSync(context.query.preview || context.query.example);
  const isPreview = !!preview;

  if (!articleId) {
    return {
      notFound: true,
    };
  }

  const article = await findArticleById(articleId);
  if (!article) {
    return {
      notFound: true,
    };
  }

  if (!isPreview) {
    incrementContentView(articleId).catch((e) => context.req.log.warn(e));
  }

  const pages = article.article_page.map((p) => ({
    articleId: p.article_id,
    pageNumber: p.page_number,
    slideText: p.slide_text,
    imgL: p.img_l,
  }));

  return {
    props: {
      article: {
        articleId: article.article_id,
        discloseScope: article.disclose_scope as DiscloseScope,
        type: article.article_type === 'VIDEO' ? 'VIDEO' : 'SLIDE',
        pageCount: article.page_count!,
        updateDateEpochMilliseconds: article.update_date.getTime(),
        pages,
      },
      isPreview,
    },
  };
};
